import React, { createContext, useContext, ReactNode } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Route, RouterContextType, RouteParams } from '../../types';

const RouterContext = createContext<RouterContextType | null>(null);

interface RouterProviderProps {
    children: ReactNode;
    routes: Route[];
}

export function RouterProvider({ children, routes }: RouterProviderProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<RouteParams>();
    const [searchParams] = useSearchParams();

    const currentRoute = routes.find(route => {
        if (route.path === location.pathname) return true;

        // Para rutas dinámicas, verificar si coincide el patrón
        if (route.isDynamic) {
            const routeSegments = route.path.split('/');
            const pathSegments = location.pathname.split('/');

            if (routeSegments.length !== pathSegments.length) return false;

            for (let i = 0; i < routeSegments.length; i++) {
                const routeSegment = routeSegments[i];
                const pathSegment = pathSegments[i];

                if (routeSegment.startsWith('[') && routeSegment.endsWith(']')) {
                    continue; // Es un parámetro dinámico
                }

                if (routeSegment !== pathSegment) return false;
            }

            return true;
        }

        return false;
    }) || null;

    const contextValue: RouterContextType = {
        routes,
        currentRoute,
        navigate: (path: string) => navigate(path),
        params: (params || {}) as RouteParams,
        searchParams: Object.fromEntries(searchParams.entries())
    };

    return React.createElement(
        RouterContext.Provider,
        { value: contextValue },
        children
    );
}

export function useAutoRouter(): RouterContextType {
    const context = useContext(RouterContext);
    if (!context) {
        throw new Error('useAutoRouter must be used within a RouterProvider');
    }
    return context;
}
